import { signInMethodType } from '@/types/auth';
import { OAuthCredential } from '@firebase/auth-types';
import type { FirebaseError } from 'firebase/app';

export type AppType = 'pickleball_studio' | 'studio' | 'live';

const APP_TYPES: readonly AppType[] = ['pickleball_studio', 'studio', 'live'];

const isAppType = (value: unknown): value is AppType =>
  typeof value === 'string' && (APP_TYPES as readonly string[]).includes(value);

export interface dataTypes {
  credentialForLinkAccount: OAuthCredential | null;
  queries: {
    api: string | null;
    callbackUrl: URL | null;
    idToken: string | null;
    platform: string | null;
    refreshToken: string | null;
    signUpEmail: string | null;
    uid: string | null;
    userName: string | null;
    linkedMethod: signInMethodType | null;
    traceparent: string | null;
    isLiveApp: boolean | null;
    appType: AppType | null;
    skipOnboarding: string | null;
  };
}

const SOCIAL_SIGN_IN_ERROR_KEY = 'socialSignInError';
const CALLBACK_URL_KEY = 'callbackUrl';
const APP_TYPE_KEY = 'appType';

// SessionStorage에서 callbackUrl 복구
const loadCallbackUrlFromStorage = (): URL | null => {
  try {
    const stored = sessionStorage.getItem(CALLBACK_URL_KEY);
    if (stored) {
      return new URL(stored);
    }
  } catch (error) {
    console.error('Failed to load callbackUrl from sessionStorage:', error);
    sessionStorage.removeItem(CALLBACK_URL_KEY);
  }
  return null;
};

// SessionStorage에서 appType 복구
const loadAppTypeFromStorage = (): AppType | null => {
  try {
    const stored = sessionStorage.getItem(APP_TYPE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (isAppType(parsed)) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Failed to load appType from sessionStorage:', error);
    sessionStorage.removeItem(APP_TYPE_KEY);
  }
  return null;
};

// SessionStorage에서 socialSignInError 복구
const loadSocialSignInErrorFromStorage = (): FirebaseError | null => {
  try {
    const stored = sessionStorage.getItem(SOCIAL_SIGN_IN_ERROR_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // FirebaseError 재생성 (code, message, customData만 있으면 됨)
      return parsed as FirebaseError;
    }
  } catch (error) {
    console.error(
      'Failed to load socialSignInError from sessionStorage:',
      error,
    );
    sessionStorage.removeItem(SOCIAL_SIGN_IN_ERROR_KEY);
  }
  return null;
};

// OAuthCredential은 클래스 인스턴스라 직렬화 불가능
// 런타임 메모리에만 유지 (페이지 이동 시 사라짐)
const sharedData: dataTypes = {
  credentialForLinkAccount: null,
  queries: {
    api: null,
    callbackUrl: loadCallbackUrlFromStorage(),
    idToken: null,
    platform: null,
    refreshToken: null,
    signUpEmail: null,
    uid: null,
    userName: null,
    linkedMethod: null,
    traceparent: null,
    isLiveApp: null,
    appType: loadAppTypeFromStorage(),
    // sessionStorage에 저장하지 않고 현재 페이지 URL에서만 읽는다.
    // (지속되면 같은 탭에서 다른 서비스 로그인 시 온보딩 스킵이 누수됨)
    skipOnboarding: null,
  },
};

const searchParamKeys: Array<keyof dataTypes['queries']> = [
  'api',
  'callbackUrl',
  'idToken',
  'platform',
  'refreshToken',
  'signUpEmail',
  'uid',
  'userName',
  'linkedMethod',
  'traceparent',
  'isLiveApp',
  'appType',
  'skipOnboarding',
];

const setCredentialForLinkAccount = (
  credential: dataTypes['credentialForLinkAccount'],
) => {
  // 런타임 메모리에만 저장 (직렬화 불가능)
  sharedData.credentialForLinkAccount = credential;
};

const clearCredentialForLinkAccount = () => {
  sharedData.credentialForLinkAccount = null;
};

const setQueryParam = <K extends keyof dataTypes['queries']>(
  key: K,
  value: dataTypes['queries'][K],
) => {
  sharedData.queries[key] = value;

  // callbackUrl은 sessionStorage에도 저장
  if (key === 'callbackUrl' && value) {
    try {
      sessionStorage.setItem(CALLBACK_URL_KEY, (value as URL).href);
    } catch (error) {
      console.error('Failed to save callbackUrl to sessionStorage:', error);
    }
  }

  if (key === 'isLiveApp' && value) {
    try {
      sessionStorage.setItem('isLiveApp', JSON.stringify(value));
      sessionStorage.setItem(APP_TYPE_KEY, JSON.stringify('live')); // isLiveApp이 true면 appType도 live로 저장
    } catch (error) {
      console.error('Failed to save isLiveApp to sessionStorage:', error);
    }
  }

  if (key === 'appType' && isAppType(value)) {
    try {
      sessionStorage.setItem(APP_TYPE_KEY, JSON.stringify(value));
    } catch (error) {
      console.error('Failed to save appType to sessionStorage:', error);
    }
  }
};

let socialSignInError: FirebaseError | null =
  loadSocialSignInErrorFromStorage();

const setSocialSignInError = (error: FirebaseError) => {
  socialSignInError = error;

  try {
    // 필요한 속성만 저장 (메서드는 제외)
    const errorData = {
      code: error.code,
      message: error.message,
      customData: error.customData,
    };
    sessionStorage.setItem(SOCIAL_SIGN_IN_ERROR_KEY, JSON.stringify(errorData));
  } catch (err) {
    console.error('Failed to save socialSignInError to sessionStorage:', err);
  }
};

const clearSocialSignInError = () => {
  socialSignInError = null;
  sessionStorage.removeItem(SOCIAL_SIGN_IN_ERROR_KEY);
};

export {
  searchParamKeys,
  sharedData,
  socialSignInError,
  setQueryParam,
  setCredentialForLinkAccount,
  clearCredentialForLinkAccount,
  setSocialSignInError,
  clearSocialSignInError,
  loadAppTypeFromStorage,
};
